import { useQuery } from "@tanstack/react-query";
import type { PortalStockRow } from "@wms/domain";
import { Input, Select, Space, Table, Tag } from "antd";
import { useState } from "react";
import type { ReactNode } from "react";
import { portalApi } from "../api/portal";
import { formatDate, formatQty } from "../api/format";
import { HelpHint } from "../components/HelpHint";
import { PortalProblem } from "./PortalLayout";
import { usePortalStore } from "./store";

/** FULL0211-B08: a column title with its «(?)» — what the number counts, in the words of the client. */
function hinted(title: string, text: ReactNode) {
  return (
    <span style={{ whiteSpace: "nowrap" }}>
      {title}
      <HelpHint subject={title} text={text} size={12} />
    </span>
  );
}

/** C04 / SEC-03 / SEC-06: own balances only — physical, available, reserved, collected, quarantine; zones, not addresses. */
export function PortalStockPage() {
  const me = usePortalStore((s) => s.me);
  const can = usePortalStore((s) => s.can);
  const [q, setQ] = useState("");
  const [warehouse, setWarehouse] = useState<string | undefined>();
  // CW31-B15: a role without «собственные остатки» does not ask the server at all; the server refuses it anyway
  const allowed = can("portal.stock.read");
  const stock = useQuery({
    queryKey: ["portal", "stock", q, warehouse],
    queryFn: () => portalApi.get<PortalStockRow[]>("/stock", { q: q || undefined, warehouse_id: warehouse }),
    enabled: allowed,
  });
  const columns = [
    { title: "Товар", key: "product", render: (_: unknown, r: PortalStockRow) => `${r.product.code} — ${r.product.name}` },
    { title: "Партия", dataIndex: "batch", render: (v: string | null) => v ?? "" },
    { title: "Срок годности", dataIndex: "expiry_date", render: (v: string | null) => (v ? formatDate(v) : "") },
    { title: "Состояние", dataIndex: "state", render: (v: string | null, r: PortalStockRow) => (v ? <Tag color={r.state_kind === "usable" ? "green" : "red"}>{v}</Tag> : "") },
    { title: "Ед.", dataIndex: "unit" },
    // FULL0211-B08: the whole physical quantity stays in «На складе»; what is taken by orders is shown apart — still in
    // storage («Резерв») or already picked and waiting for the shipment («Собрано») — and is not offered as «Доступно»
    { title: hinted("На складе", "Всё ваше количество на складе: доступное, резерв, собранное под заказы, карантин и брак."), dataIndex: "physical", align: "right" as const, render: (v: unknown) => formatQty(v) },
    { title: hinted("Доступно", "Можно заказать: на складе минус резерв под заказы, собранное под заказы, карантин и брак."), dataIndex: "available", align: "right" as const, render: (v: unknown) => formatQty(v) },
    { title: hinted("Резерв под заказы", "Отложено под заказы и задания склада: товар ещё не отобран и лежит на своём месте."), dataIndex: "reserved", align: "right" as const, render: (v: unknown) => formatQty(v) },
    { title: hinted("Собрано под заказы", "Уже отобрано под заказы и ждёт отправки в зоне отгрузки, экспедиции или упаковки. Уменьшается по мере отгрузки. Если заказ отменён, товар остаётся здесь, пока склад не вернёт его на хранение."), dataIndex: "collected", align: "right" as const, render: (v: unknown) => formatQty(v) },
    { title: "Карантин / брак", dataIndex: "quarantine", align: "right" as const, render: (v: unknown) => formatQty(v) },
    { title: me?.visibility === "address" ? "Адреса" : "Зоны", dataIndex: "places", render: (v: string[]) => v.join(", ") },
  ];
  const header = (
    <div className="wms-page-header">
      <div>
        <h1>Остатки</h1>
        <div className="wms-subtitle">Физический и доступный остаток раздельно; резерв, собранное под заказы, карантин и брак в доступное не входят</div>
      </div>
    </div>
  );
  // CW31-B15: loading, a refusal, a failure and a real empty answer are four different screens — a refusal or a
  // failure never says «товара нет» and never shows rows kept from an earlier answer
  if (!allowed || stock.isError) {
    return (
      <div>
        {header}
        <PortalProblem
          forbidden={!allowed}
          error={stock.error}
          deniedTitle="Просмотр остатков недоступен вашей роли"
          deniedHint="Это не значит, что вашего товара на складе нет: список остатков просто не входит в права вашей роли в кабинете."
          failedTitle="Не удалось загрузить остатки"
          onRetry={() => stock.refetch()}
        />
      </div>
    );
  }
  const filtered = !!q || !!warehouse;
  return (
    <div>
      {header}
      <Space wrap style={{ marginBottom: 12 }}>
        <Input.Search placeholder="Товар, артикул, партия или штрихкод" value={q} onChange={(e) => setQ(e.target.value)} allowClear style={{ width: 320 }} data-testid="portal-stock-search" />
        {(me?.warehouses.length ?? 0) > 1 && <Select allowClear placeholder="Все мои склады" value={warehouse} onChange={setWarehouse} options={(me?.warehouses ?? []).map((w) => ({ value: w.id, label: w.name }))} style={{ minWidth: 220 }} />}
      </Space>
      <Table<PortalStockRow>
        rowKey={(r) => `${r.product.id}:${r.batch}:${r.state}`}
        size="middle"
        loading={stock.isLoading}
        dataSource={stock.data ?? []}
        columns={columns}
        pagination={{ pageSize: 50 }}
        scroll={{ x: true }}
        locale={{
          emptyText: stock.isLoading
            ? "Загружаем остатки…"
            : filtered
              ? "По этому запросу вашего товара не найдено: измените поиск или склад"
              : "Вашего товара с подтверждённой принадлежностью на складе нет",
        }}
        data-testid="portal-stock-table"
      />
    </div>
  );
}
